/**
 * Send Invitation Modal
 *
 * 3-step wizard for sending recruitment invitation emails via Gmail
 * Follows the same pattern as Pre-Approval send modal
 */

import React, { useState, useEffect } from 'react';
import { LwModal, LwField } from '@/components/lw';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Mail, ChevronRight, ChevronLeft, Check, AlertCircle } from 'lucide-react';
import type { RecruitmentCandidate } from './types';
import { RecruitmentInvitationService } from '@/services/recruitmentInvitationService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('SendInvitationModal', 'recruitment');

interface SendInvitationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: RecruitmentCandidate;
  onSendSuccess?: (invitationId: string) => void;
}

type WizardStep = 1 | 2 | 3;

export const SendInvitationModal: React.FC<SendInvitationModalProps> = ({
  open,
  onOpenChange,
  candidate,
  onSendSuccess
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [isSending, setIsSending] = useState(false);

  // Form data
  const [recipientEmail, setRecipientEmail] = useState(candidate.email);
  const [ccEmails, setCcEmails] = useState('');
  const [expirationDays, setExpirationDays] = useState(14);
  const [message, setMessage] = useState('');

  // Get position from candidate, defaulting to 'Loan Officer'
  const position = 'Loan Officer'; // Since position field isn't in current schema

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setRecipientEmail(candidate.email);
      setCcEmails('');
      setExpirationDays(14);
      setMessage(RecruitmentInvitationService.getDefaultMessage(candidate.firstName, position));
    }
  }, [open, candidate.email, candidate.firstName, position]);

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep((currentStep + 1) as WizardStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as WizardStep);
    }
  };

  const handleSend = async () => {
    const timer = log.startTimer('send_invitation');
    setIsSending(true);

    try {
      log.info('Sending invitation email', {
        candidateId: candidate.id,
        candidateEmail: recipientEmail
      });

      const result = await RecruitmentInvitationService.sendInvitation(
        candidate.id,
        {
          candidateEmail: recipientEmail,
          candidateName: `${candidate.firstName} ${candidate.lastName}`,
          candidateFirstName: candidate.firstName,
          candidateLastName: candidate.lastName,
          message,
          ccEmails: ccEmails || undefined,
          expirationDays,
          emailType: 'invitation'
        }
      );

      if (result.success) {
        log.endTimer(timer, { success: true, invitationId: result.invitationId });

        if (result.warning) {
          // Partial success — e.g. the invite sent but recording the invite
          // sender failed, so recruiter notifications were skipped.
          toast.warning('Invitation sent, with a warning', {
            description: result.warning
          });
        } else {
          toast.success('Invitation sent successfully!', {
            description: `Email sent to ${recipientEmail}`
          });
        }

        if (onSendSuccess && result.invitationId) {
          onSendSuccess(result.invitationId);
        }

        onOpenChange(false);
      } else {
        throw new Error(result.error || 'Failed to send invitation');
      }
    } catch (error) {
      log.error('Failed to send invitation', { candidateId: candidate.id }, error as Error);
      log.endTimer(timer, { success: false });

      toast.error('Failed to send invitation', {
        description: error instanceof Error ? error.message : 'Please try again or contact support'
      });
    } finally {
      setIsSending(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Recipient Information';
      case 2:
        return 'Customize Message';
      case 3:
        return 'Review & Send';
      default:
        return '';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1:
        return 'Enter the recipient email and invitation settings';
      case 2:
        return 'Personalize your invitation message';
      case 3:
        return 'Review your invitation before sending';
      default:
        return '';
    }
  };

  if (!open) {
    return null;
  }

  return (
    <LwModal
      size="wide"
      eyebrow="Recruiting"
      title="Send Invitation Email"
      sub={`Send a recruitment invitation to ${candidate.firstName} ${candidate.lastName}`}
      onClose={() => onOpenChange(false)}
      footer={
        <>
          {currentStep > 1 && (
            <button
              className="btn-sm"
              style={{ marginRight: 'auto' }}
              onClick={handleBack}
              disabled={isSending}
              type="button"
            >
              <ChevronLeft />
              Back
            </button>
          )}
          <button className="btn-sm" onClick={() => onOpenChange(false)} disabled={isSending} type="button">
            Cancel
          </button>
          {currentStep < 3 ? (
            <button className="btn-sm solid" onClick={handleNext} disabled={!recipientEmail} type="button">
              Next
              <ChevronRight />
            </button>
          ) : (
            <button
              className="btn-sm solid"
              onClick={handleSend}
              disabled={isSending || !recipientEmail}
              type="button"
            >
              {isSending ? (
                <>
                  <Loader2 className="animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Mail />
                  Send Invitation
                </>
              )}
            </button>
          )}
        </>
      }
    >
      {/* Progress Indicator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {[1, 2, 3].map((step) => {
          const isCurrent = currentStep === step;
          const isDone = currentStep > step;
          return (
            <React.Fragment key={step}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    border: '2px solid',
                    fontWeight: 600,
                    fontSize: 13,
                    borderColor: isCurrent || isDone ? 'transparent' : 'var(--line)',
                    background: isCurrent || isDone ? 'var(--green-700)' : 'transparent',
                    color: isCurrent || isDone ? '#EFE9D9' : 'var(--lw-muted)'
                  }}
                >
                  {isDone ? <Check size={15} /> : step}
                </div>
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: isCurrent ? 'var(--green-800)' : 'var(--lw-muted)'
                  }}
                >
                  {step === 1 ? 'Recipient' : step === 2 ? 'Message' : 'Review'}
                </span>
              </div>
              {step < 3 && <ChevronRight size={16} style={{ color: 'var(--line-strong, var(--line))', margin: '0 4px' }} />}
            </React.Fragment>
          );
        })}
      </div>

      <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 16 }}>
        <h3 style={{ fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 500, color: 'var(--green-900)', margin: '0 0 4px' }}>
          {getStepTitle()}
        </h3>
        <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: '0 0 18px' }}>{getStepDescription()}</p>

        {/* Step 1: Recipient Information */}
        {currentStep === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label htmlFor="candidate-name">Candidate Name</label>
              <input
                id="candidate-name"
                value={`${candidate.firstName} ${candidate.lastName}`}
                disabled
                style={{ background: '#F0ECE0', color: 'var(--ink-soft)' }}
              />
            </div>

            <div className="field">
              <label htmlFor="recipient-email">
                Recipient Email <span className="req">*</span>
              </label>
              <input
                id="recipient-email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="candidate@example.com"
              />
            </div>

            <div className="field">
              <label htmlFor="cc-emails">CC (Optional)</label>
              <input
                id="cc-emails"
                type="email"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="hr@lendwisemtg.com"
              />
              <span className="hint">Separate multiple emails with commas</span>
            </div>

            <LwField label="Invitation Expiration" htmlFor="expiration-days">
              <Select value={expirationDays.toString()} onValueChange={(value) => setExpirationDays(parseInt(value))}>
                <SelectTrigger id="expiration-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="lw-ds">
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days (recommended)</SelectItem>
                  <SelectItem value="21">21 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </LwField>
          </div>
        )}

        {/* Step 2: Customize Message */}
        {currentStep === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label htmlFor="message">Invitation Message</label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={12}
                placeholder="Enter your personalized message..."
                style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="hint">{message.length} / 2000 characters</span>
                <button
                  type="button"
                  className="btn-sm"
                  style={{ height: 28, padding: '0 10px', fontSize: 12 }}
                  onClick={() =>
                    setMessage(RecruitmentInvitationService.getDefaultMessage(candidate.firstName, position))
                  }
                >
                  Reset to Default
                </button>
              </div>
            </div>

            <div className="panel" style={{ display: 'flex', gap: 10, padding: 12 }}>
              <AlertCircle size={18} style={{ color: 'var(--gold-600)', flex: 'none', marginTop: 1 }} />
              <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
                <p style={{ fontWeight: 600, margin: '0 0 4px' }}>Email will include:</p>
                <ul style={{ listStyle: 'disc', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2, margin: 0 }}>
                  <li>Your custom message above</li>
                  <li>Call-to-action button with invitation link</li>
                  <li>Next steps (complete profile, upload docs, sign agreement)</li>
                  <li>Expiration notice ({expirationDays} days)</li>
                  <li>Your signature</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review & Send */}
        {currentStep === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--lw-muted)', margin: 0 }}>Candidate</p>
                  <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>
                    {candidate.firstName} {candidate.lastName}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--lw-muted)', margin: 0 }}>To</p>
                  <p style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', margin: 0 }}>{recipientEmail}</p>
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--lw-muted)', margin: 0 }}>Expires In</p>
                  <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{expirationDays} days</p>
                </div>
              </div>

              {ccEmails && (
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--lw-muted)', margin: 0 }}>CC</p>
                  <p style={{ fontSize: 13, wordBreak: 'break-all', margin: 0 }}>{ccEmails}</p>
                </div>
              )}
            </div>

            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)', display: 'block', marginBottom: 8 }}>
                Message Preview
              </label>
              <div
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 9,
                  padding: 14,
                  background: 'var(--lw-card)',
                  maxHeight: 300,
                  overflowY: 'auto'
                }}
              >
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: 'var(--ink)', margin: 0 }}>
                  {message}
                </pre>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                background: '#F7F0DC',
                border: '1px solid #E4D2A0',
                borderRadius: 11,
                padding: '12px 14px'
              }}
            >
              <AlertCircle size={18} style={{ color: '#8A6420', flex: 'none', marginTop: 1 }} />
              <div style={{ fontSize: 12.5, color: '#7E6426' }}>
                <p style={{ fontWeight: 600, margin: 0 }}>Ready to send</p>
                <p style={{ marginTop: 4, marginBottom: 0 }}>
                  This invitation will be sent from your Gmail account. The candidate will receive a professional email
                  with a secure link to complete their onboarding.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </LwModal>
  );
};
